class MainOfferPicker {
  constructor(picker, sectionData) {
    this.picker = picker;
    this.sectionId = picker.dataset.sectionId;
    this.productId = sectionData.productId;
    this.productType = sectionData.productType || '';
    this.onetimeDiscountCode = sectionData.onetimeDiscountCode;
    this.subscriptionDiscountCode = sectionData.subscriptionDiscountCode;
    this.onetimeDiscountPercent = sectionData.onetimeDiscountPercent;
    this.subscriptionDiscountPercent = sectionData.subscriptionDiscountPercent;
    this.hasSellingPlans = sectionData.hasSellingPlans;
    this.bonusVariantId = sectionData.bonusVariantId || null;
    this.bonusVariantId2 = sectionData.bonusVariantId2 || null;
    this.directCheckout = sectionData.directCheckout !== false;
    this.simpleOnetimeComparePrice = sectionData.simpleOnetimeComparePrice || 0;
    this.simpleOnetimeSalePrice = sectionData.simpleOnetimeSalePrice || 0;

    this.selectedVariantId = null;
    this.selectedSellingPlanId = null;
    this.selectedWeight = null;
    this.selectedIntervalDays = null;
    this.quantity = 1;
    
    // Get elements
    this.variantDropdown = this.picker.querySelector('[data-variant-dropdown]');
    this.onetimePriceEl = this.picker.querySelector('[data-price-onetime]');
    this.subscriptionPriceEl = this.picker.querySelector('[data-price-subscription]');
    this.subscriptionOriginalPriceEl = this.picker.querySelector('[data-price-original-subscription]');
    this.onetimeOriginalPriceEl = this.picker.querySelector('[data-price-original-onetime]');
    this.deliveryFrequencyEl = this.picker.querySelector('[data-delivery-frequency]');
    this.ctaPriceEl = this.picker.querySelector('[data-cta-price]');
    this.addToCartBtn = this.picker.querySelector('[data-add-to-cart-checkout]');
    
    // Store button subtext for restoration
    // We'll try to get it in resetButtonState() if needed
    
    // Process benefits: replace [selling_plan_name] placeholder
    this.benefitsContent = this.picker.querySelector('[data-benefits-content]');
    if (this.benefitsContent) {
      this.benefitsContent.innerHTML = this.benefitsContent.innerHTML.replace(
        /\[selling_plan_name\]/gi,
        '<span data-selling-plan-name>--</span>'
      );
    }
    
    this.init();
  }
  
  init() {
    this.resetButtonState();
    this.initVariantDropdown();
    this.initPurchaseTypeRadios();
    this.initAddToCartButton();
    this.initDeliveryBreakdown();
    this.updateSelection();
    this.updatePrices();
  }
  
  resetButtonState() {
    if (!this.addToCartBtn) return;

    // Reset button to enabled state
    this.addToCartBtn.disabled = false;

    // Get subtext: data attribute (from Liquid) > existing DOM > stored > default
    let subtext = this.addToCartBtn.getAttribute('data-button-subtext');
    if (subtext === null) {
      const subtextEl = this.addToCartBtn.querySelector('.main-offer__cta-line-2');
      if (subtextEl && !this.addToCartBtn.innerHTML.includes('Processing...')) {
        subtext = subtextEl.textContent;
      } else if (this.buttonSubtext) {
        subtext = this.buttonSubtext;
      } else {
        subtext = 'Results Guaranteed 🔒';
      }
    }

    // Store subtext for future use
    this.buttonSubtext = subtext;

    // Restore button HTML structure
    const subtextHtml = subtext ? `<div class="main-offer__cta-line-2">${subtext}</div>` : '';
    this.addToCartBtn.innerHTML = `
      <div class="main-offer__cta-line-1">
        <span class="main-offer__cta-price" data-cta-price>$0.00</span>
        <span>-</span>
        <span>ADD TO CART</span>
      </div>
      ${subtextHtml}
    `;

    // Re-get the CTA price element since we replaced the HTML
    this.ctaPriceEl = this.picker.querySelector('[data-cta-price]');
  }
  
  initVariantDropdown() {
    if (!this.variantDropdown) return;
    
    const trigger = this.variantDropdown.querySelector('[data-dropdown-trigger]');
    const triggerText = this.variantDropdown.querySelector('[data-trigger-text]');
    const options = this.variantDropdown.querySelector('[data-dropdown-options]');
    const inputs = this.variantDropdown.querySelectorAll('input[type="radio"]');
    
    if (!trigger || !options) return;
    
    // Toggle dropdown on trigger click
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const isOpen = this.variantDropdown.classList.contains('is-open');
      
      // Close all variant dropdowns on the page
      document.querySelectorAll('[data-variant-dropdown].is-open').forEach(d => {
        d.classList.remove('is-open');
      });
      
      // Toggle current dropdown
      if (!isOpen) {
        this.variantDropdown.classList.add('is-open');
      }
    });
    
    // Handle variant selection
    inputs.forEach(input => {
      const label = this.variantDropdown.querySelector(`label[for="${input.id}"]`);
      
      if (label) {
        label.addEventListener('click', (e) => {
          e.preventDefault();

          input.checked = true;
          triggerText.textContent = input.value;
          this.variantDropdown.classList.remove('is-open');

          // Fire a bubbling change event so external listeners (e.g. the
          // lp-offer-cta per-variant perk text updaters) can react to the
          // new selection. Setting `.checked = true` in JS does not fire
          // this natively, so we dispatch it manually.
          input.dispatchEvent(new Event('change', { bubbles: true }));

          this.updateSelection();
          this.updatePrices();
        });
      }
    });
  }
  
  initPurchaseTypeRadios() {
    if (!this.hasSellingPlans) return;
    
    const purchaseRadios = this.picker.querySelectorAll('.main-offer__purchase-radio');
    purchaseRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.updateSelection();
        this.updatePrices();
      });
    });
  }
  
  formatWeight(weight) {
    if (!weight) return null;
    // Convert to lowercase and replace spaces with hyphens
    return weight.toLowerCase().replace(/\s+/g, '-');
  }
  
  initAddToCartButton() {
    if (!this.addToCartBtn) return;

    this.addToCartBtn.addEventListener('click', async () => {
      if (!this.selectedVariantId) {
        alert('Please select a variant');
        return;
      }
      if (this.directCheckout) {
        await this.handleDirectCheckout();
      } else {
        await this.handleAddToCart();
      }
    });
  }

  async handleDirectCheckout() {
    this.addToCartBtn.disabled = true;
    const originalContent = this.addToCartBtn.innerHTML;
    this.addToCartBtn.innerHTML = '<div class="main-offer__cta-line-1">Processing...</div>';

    try {
      // Build cart attributes
      const activePurchaseType = this.getActivePurchaseType();
      const attributes = {};

      if (this.productType) {
        const prefix = this.productType;

        // Add weight attribute (formatted)
        if (this.selectedWeight) {
          const formattedWeight = this.formatWeight(this.selectedWeight);
          if (formattedWeight) {
            attributes[`${prefix}-weight`] = formattedWeight;
          }
        }

        // Add quantity attribute
        attributes[`${prefix}-quantity`] = this.quantity.toString();

        // Add interval attribute (only for subscription)
        if (activePurchaseType === 'subscription' && this.selectedIntervalDays) {
          attributes[`${prefix}-interval`] = `${this.selectedIntervalDays}-days`;
        }

        // Add purchase type attribute
        attributes[`${prefix}-purchase-type`] = activePurchaseType === 'subscription' ? 'sub' : 'otp';

        // Add source type attribute (offer for main-offer)
        attributes[`${prefix}-source-type`] = 'offer';
      }

      // Step 1: Clear cart (single request via /cart/clear.js)
      await this.clearCart();

      // Step 2: Add all products in a single /cart/add.js call
      const items = [{
        id: this.selectedVariantId,
        quantity: this.quantity,
        ...(this.selectedSellingPlanId && { selling_plan: this.selectedSellingPlanId })
      }];
      // Free gifts only apply to subscription — one-time purchases ship alone.
      if (activePurchaseType === 'subscription') {
        if (this.bonusVariantId) items.push({ id: this.bonusVariantId, quantity: 1 });
        if (this.bonusVariantId2) items.push({ id: this.bonusVariantId2, quantity: 1 });
      }

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add to cart');
      }

      // Step 3: Fire cart attributes + discount iframe in parallel, then redirect
      const discountCode = activePurchaseType === 'subscription'
        ? this.subscriptionDiscountCode
        : this.onetimeDiscountCode;

      const parallel = [];

      // Update cart attributes if any
      if (Object.keys(attributes).length > 0) {
        parallel.push(fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes })
        }));
      }

      // Apply discount code via hidden iframe
      if (discountCode && discountCode.trim() !== '') {
        parallel.push(new Promise((resolve) => {
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          let settled = false;
          const done = () => { if (!settled) { settled = true; if (iframe.parentNode) document.body.removeChild(iframe); resolve(); } };
          iframe.onload = done;
          iframe.onerror = done;
          iframe.src = '/discount/' + encodeURIComponent(discountCode.trim());
          document.body.appendChild(iframe);
          setTimeout(done, 3000); // Safety timeout
        }));
      }

      await Promise.all(parallel);
      window.location.href = '/checkout';

    } catch (error) {
      console.error('Error:', error);
      alert('Error processing your order. Please try again.');
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.innerHTML = originalContent;
    }
  }

  async handleAddToCart() {
    this.addToCartBtn.disabled = true;
    const originalContent = this.addToCartBtn.innerHTML;
    this.addToCartBtn.innerHTML = '<div class="main-offer__cta-line-1">Adding...</div>';

    try {
      const activePurchaseType = this.getActivePurchaseType();

      // Build item
      const item = {
        id: parseInt(this.selectedVariantId),
        quantity: this.quantity,
      };
      if (activePurchaseType === 'subscription' && this.selectedSellingPlanId) {
        item.selling_plan = parseInt(this.selectedSellingPlanId);
      }

      // Detect cart drawer / notification element
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      // Build request body — include sections inline (standard Dawn pattern)
      // so /cart/add.js returns both item data AND rendered section HTML
      const body = { items: [item] };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(s => s.id);
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(window.Shopify?.routes?.root
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Request failed (' + response.status + ')');
      }

      const data = await response.json();

      // Publish cart update event for header badge etc.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'main-offer-picker',
          productVariantId: this.selectedVariantId,
          cartData: data,
        });
      }

      // Open the cart drawer with the response data (contains sections HTML)
      if (cart && typeof cart.renderContents === 'function' && data.sections) {
        cart.renderContents(data);
        // Remove is-empty class so CSS doesn't collapse the line-items area.
        // Dawn's product-form.js does this in .finally() — we must do it too.
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      } else {
        // No cart drawer — redirect to cart page
        window.location = (window.routes && window.routes.cart_url) || '/cart';
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      alert('Error adding to cart. Please try again.\n(' + error.message + ')');
    } finally {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.innerHTML = originalContent;
      this.ctaPriceEl = this.picker.querySelector('[data-cta-price]');
    }
  }
  
  async clearCart() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
      throw new Error('Failed to clear cart');
    }
    return await response.json();
  }
  
  initDeliveryBreakdown() {
    const breakdownEl = this.picker.querySelector('.main-offer__delivery-breakdown[data-delivery-epoch]');
    if (!breakdownEl) return;
    
    const deliveryEpoch = breakdownEl.dataset.deliveryEpoch;
    const deliveryDays = parseInt(breakdownEl.dataset.deliveryDays);
    
    if (!deliveryEpoch || isNaN(deliveryEpoch) || isNaN(deliveryDays)) return;
    
    const deliveryDate = new Date(parseInt(deliveryEpoch) * 1000);
    const day = deliveryDate.getDate();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const month = monthNames[deliveryDate.getMonth()];
    
    let dayOrdinal = day.toString();
    if (day === 1 || day === 21 || day === 31) dayOrdinal += 'st';
    else if (day === 2 || day === 22) dayOrdinal += 'nd';
    else if (day === 3 || day === 23) dayOrdinal += 'rd';
    else dayOrdinal += 'th';
    
    const formattedDate = '<strong>' + dayOrdinal + ' of ' + month + '</strong>';
    const textEl = breakdownEl.querySelector('.delivery-breakdown-text');
    if (textEl) {
      let html = textEl.innerHTML;
      html = html.replace(/\[days_since_today\]/g, formattedDate);
      textEl.innerHTML = html;
    }
  }
  
  getActivePurchaseType() {
    if (!this.hasSellingPlans) return 'onetime';
    
    const checkedRadio = this.picker.querySelector('.main-offer__purchase-radio:checked');
    if (!checkedRadio) return 'onetime';
    
    const purchaseOption = checkedRadio.closest('[data-purchase-type]');
    return purchaseOption ? purchaseOption.dataset.purchaseType : 'onetime';
  }
  
  updateSelection() {
    if (!this.variantDropdown) return;
    
    const selectedVariant = this.variantDropdown.querySelector('input[type="radio"]:checked');
    if (!selectedVariant) return;
    
    this.selectedVariantId = selectedVariant.getAttribute('data-variant-id');
    // Store the weight (variant option value)
    this.selectedWeight = selectedVariant.value || null;
    
    // Calculate selling plan for subscription
    const activePurchaseType = this.getActivePurchaseType();
    if (activePurchaseType === 'subscription' && this.hasSellingPlans) {
      // Get selling plan ID from variant data attributes (they're included when purchase_type is 'subscription')
      const sellingPlanId = selectedVariant.getAttribute('data-selling-plan-id');
      const sellingPlanName = selectedVariant.getAttribute('data-selling-plan-name');
      const intervalDays = selectedVariant.getAttribute('data-interval-days');
      
      this.selectedSellingPlanId = sellingPlanId || null;
      this.selectedIntervalDays = intervalDays || null;
      
      // Update delivery frequency
      if (this.deliveryFrequencyEl && intervalDays) {
        this.deliveryFrequencyEl.textContent = `Delivery every ${intervalDays} ${intervalDays === '1' ? 'day' : 'days'}`;
      }
      
      // Update benefits text
      if (this.benefitsContent) {
        const nameSpan = this.benefitsContent.querySelector('[data-selling-plan-name]');
        if (nameSpan && sellingPlanName) {
        nameSpan.textContent = sellingPlanName;
        }
        const discountSpan = this.benefitsContent.querySelector('[data-selling-plan-discount]');
        const discount = selectedVariant.getAttribute('data-selling-plan-discount');
        if (discountSpan && discount) {
        discountSpan.textContent = discount;
      }
    }
    } else {
      this.selectedSellingPlanId = null;
      this.selectedIntervalDays = null;
      // Clear delivery frequency if not subscription
      if (this.deliveryFrequencyEl) {
        this.deliveryFrequencyEl.textContent = '';
      }
    }
  }
  
  formatPrice(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }
  
  calculateDiscountedPrice(originalPrice, discountPercent) {
    const discountAmount = (originalPrice * discountPercent) / 100;
    // Round down (floor) to ensure we don't round up the discounted price
    return Math.floor(originalPrice - discountAmount);
  }
  
  updatePrices() {
    if (!this.variantDropdown) return;
    
    const selectedVariant = this.variantDropdown.querySelector('input[type="radio"]:checked');
    if (!selectedVariant) {
      // Clear prices if no variant selected
      if (this.onetimePriceEl) this.onetimePriceEl.textContent = '';
      if (this.subscriptionPriceEl) this.subscriptionPriceEl.textContent = '';
      if (this.ctaPriceEl) this.ctaPriceEl.textContent = '';
      return;
    }
    
    const variantPrice = parseInt(selectedVariant.getAttribute('data-variant-price'));
    if (!variantPrice) return;
    
    const activePurchaseType = this.getActivePurchaseType();
    const discountPercent = activePurchaseType === 'subscription' 
      ? this.subscriptionDiscountPercent 
      : this.onetimeDiscountPercent;
    const discountedPrice = this.calculateDiscountedPrice(variantPrice, discountPercent);
    
    // Update purchase type prices
    if (this.hasSellingPlans) {
      if (this.onetimePriceEl) {
        let onetimeDisplayPrice, onetimeOriginalPrice;

        if (this.simpleOnetimeSalePrice > 0) {
          // Hard-coded override prices
          onetimeDisplayPrice = this.simpleOnetimeSalePrice;
          onetimeOriginalPrice = this.simpleOnetimeComparePrice > 0
            ? this.simpleOnetimeComparePrice
            : variantPrice;
        } else {
          // Calculate from discount percent
          onetimeDisplayPrice = this.calculateDiscountedPrice(variantPrice, this.onetimeDiscountPercent);
          onetimeOriginalPrice = variantPrice;
        }

        this.onetimePriceEl.textContent = this.formatPrice(onetimeDisplayPrice);

        if (this.onetimeOriginalPriceEl) {
          if (onetimeDisplayPrice < onetimeOriginalPrice) {
            this.onetimeOriginalPriceEl.textContent = this.formatPrice(onetimeOriginalPrice);
          } else {
            // Clear content — CSS :empty rule hides element and collapses margin
            this.onetimeOriginalPriceEl.textContent = '';
          }
        }
      }
      
      if (this.subscriptionPriceEl) {
          const displayPrice = this.subscriptionDiscountPercent > 0
            ? this.calculateDiscountedPrice(variantPrice, this.subscriptionDiscountPercent)
            : parseInt(selectedVariant.getAttribute('data-selling-plan-price')) || variantPrice;
          this.subscriptionPriceEl.textContent = this.formatPrice(displayPrice);

        if (this.subscriptionOriginalPriceEl) {
          if (displayPrice < variantPrice) {
          this.subscriptionOriginalPriceEl.textContent = this.formatPrice(variantPrice);
          this.subscriptionOriginalPriceEl.style.display = 'block';
          } else {
          this.subscriptionOriginalPriceEl.style.display = 'none';
          }
        }
      }
    }
    
    // Update CTA price
    if (this.ctaPriceEl) {
      if (activePurchaseType === 'subscription') {
        const ctaSubPrice = this.subscriptionDiscountPercent > 0
          ? this.calculateDiscountedPrice(variantPrice, this.subscriptionDiscountPercent)
          : parseInt(selectedVariant.getAttribute('data-selling-plan-price')) || discountedPrice;
        this.ctaPriceEl.textContent = this.formatPrice(ctaSubPrice);
      } else {
        // Use hard-coded sale price for CTA if set, otherwise calculated discount
        const ctaOnetimePrice = this.simpleOnetimeSalePrice > 0
          ? this.simpleOnetimeSalePrice
          : discountedPrice;
        this.ctaPriceEl.textContent = this.formatPrice(ctaOnetimePrice);
      }
    }

    // LP Hero Pricing (only exists in lp-offer-cta section, no-op elsewhere)
    const heroPriceEl = this.picker.querySelector('[data-lp-hero-price]');
    if (heroPriceEl) {
      const heroOriginalEl = this.picker.querySelector('[data-lp-hero-original-price]');
      const heroDiscountTextEl = this.picker.querySelector('[data-lp-hero-discount-text]');
      const heroRefillTextEl = this.picker.querySelector('[data-lp-hero-refill-text]');

      if (activePurchaseType === 'subscription') {
        const heroSubPrice = this.subscriptionDiscountPercent > 0
          ? this.calculateDiscountedPrice(variantPrice, this.subscriptionDiscountPercent)
          : parseInt(selectedVariant.getAttribute('data-selling-plan-price')) || discountedPrice;
        heroPriceEl.textContent = this.formatPrice(heroSubPrice);
        if (heroOriginalEl) {
          heroOriginalEl.textContent = this.formatPrice(variantPrice);
          heroOriginalEl.style.display = heroSubPrice < variantPrice ? '' : 'none';
        }
        if (heroDiscountTextEl) heroDiscountTextEl.style.display = '';
        if (heroRefillTextEl) heroRefillTextEl.style.display = '';
      } else {
        let heroOnetimePrice, heroOnetimeOriginal;
        if (this.simpleOnetimeSalePrice > 0) {
          heroOnetimePrice = this.simpleOnetimeSalePrice;
          heroOnetimeOriginal = this.simpleOnetimeComparePrice > 0 ? this.simpleOnetimeComparePrice : variantPrice;
        } else {
          heroOnetimePrice = this.calculateDiscountedPrice(variantPrice, this.onetimeDiscountPercent);
          heroOnetimeOriginal = variantPrice;
        }
        heroPriceEl.textContent = this.formatPrice(heroOnetimePrice);
        if (heroOriginalEl) {
          const showOriginal = heroOnetimePrice < heroOnetimeOriginal;
          heroOriginalEl.textContent = showOriginal ? this.formatPrice(heroOnetimeOriginal) : '';
          heroOriginalEl.style.display = showOriginal ? '' : 'none';
        }
        if (heroDiscountTextEl) heroDiscountTextEl.style.display = 'none';
        if (heroRefillTextEl) heroRefillTextEl.style.display = 'none';
      }
    }
  }
}

// Store picker instances for reset on page restore
const mainOfferPickerInstances = [];

function initMainOfferPickers() {
  const pickers = document.querySelectorAll('[data-main-offer-picker]');
  
  pickers.forEach(picker => {
    const sectionId = picker.dataset.sectionId;
    const sectionData = window.mainOfferData && window.mainOfferData[sectionId];
    
    if (sectionData) {
      const instance = new MainOfferPicker(picker, sectionData);
      mainOfferPickerInstances.push(instance);
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMainOfferPickers);
} else {
  initMainOfferPickers();
}

// Reset button states when page is restored from cache (back/forward navigation)
window.addEventListener('pageshow', (event) => {
  // event.persisted is true when the page is restored from bfcache
  if (event.persisted) {
    mainOfferPickerInstances.forEach(instance => {
      if (instance && typeof instance.resetButtonState === 'function') {
        instance.resetButtonState();
      }
    });
  }
});

// Close dropdowns when clicking outside
document.addEventListener('click', (e) => {
  if (!e.target.closest('[data-variant-dropdown]')) {
    document.querySelectorAll('[data-variant-dropdown].is-open').forEach(d => {
      d.classList.remove('is-open');
    });
  }
});

